import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, resolveImg } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Eye, EyeOff } from "lucide-react";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);

  const load = () => api.get("/blog/admin/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));

  useEffect(() => {
    document.title = "Admin | Distópica";
    load();
  }, []);

  const del = async (id, title) => {
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/blog/admin/posts/${id}`);
      toast.success("Artículo eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <main className="bg-[#0A0A0A] min-h-screen">
      <Toaster position="top-center" theme="dark" richColors />
      <header className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight leading-none">
            DISTÓPICA<span style={{ color: "var(--fuchsia)" }}>.</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="font-mono-x text-xs uppercase tracking-widest text-neutral-500 hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="logout-btn" className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--fuchsia)] transition-colors flex items-center gap-2">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="patch text-[var(--yellow)] -rotate-2">Blog / Gestión</span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tight mt-4">Artículos</h1>
          </div>
          <button
            onClick={() => navigate("/admin/nuevo")}
            data-testid="new-post-btn"
            className="inline-flex items-center gap-2 bg-[var(--fuchsia)] text-white font-mono-x uppercase text-sm tracking-[0.15em] px-6 py-3 rounded-sm hover:bg-[var(--yellow)] hover:text-black transition-colors"
          >
            <Plus size={18} /> Nuevo
          </button>
        </div>

        {posts === null ? (
          <p className="font-mono-x text-sm text-neutral-500">Cargando…</p>
        ) : posts.length === 0 ? (
          <p className="font-mono-x text-sm text-neutral-500" data-testid="admin-empty">Todavía no hay artículos. Creá el primero.</p>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10">
            {posts.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4 sm:p-5 hover:bg-white/[0.03] transition-colors" data-testid={`admin-row-${p.id}`}>
                <div className="w-16 h-16 shrink-0 overflow-hidden border border-white/10 hidden sm:block">
                  {p.cover_image && <img src={resolveImg(p.cover_image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display uppercase text-lg leading-tight truncate">{p.title}</p>
                  <p className="font-mono-x text-[11px] uppercase tracking-widest text-neutral-500 mt-1 flex items-center gap-3">
                    {p.published ? <span className="text-[var(--yellow)] flex items-center gap-1"><Eye size={12} /> Publicado</span> : <span className="text-neutral-500 flex items-center gap-1"><EyeOff size={12} /> Borrador</span>}
                    <span>{fmtDate(p.created_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.published && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="Ver" className="p-2 text-neutral-400 hover:text-white transition-colors">
                      <ExternalLink size={18} />
                    </a>
                  )}
                  <button onClick={() => navigate(`/admin/editar/${p.id}`)} data-testid={`edit-${p.id}`} title="Editar" className="p-2 text-neutral-400 hover:text-[var(--yellow)] transition-colors">
                    <Pencil size={18} />
                  </button>
                  <button onClick={() => del(p.id, p.title)} data-testid={`delete-${p.id}`} title="Eliminar" className="p-2 text-neutral-400 hover:text-[var(--fuchsia)] transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
